import React, { useEffect, useCallback } from 'react'
import styled from 'styled-components'
import moment, { Moment } from 'moment'
import LoaderBase from 'react-loader-spinner'
import { FormikProps } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import MultiSelect from 'components/MultiSelect'
import DayPicker from 'components/DayPicker'
import { Input, Label, Error } from 'components/Input'
import { fetchRateTypes } from 'features/rateTypes/actions'
import {
  rateTypesSelector,
  rateTypesIsLoadingSelector,
} from 'features/rateTypes/selectors'

import Step from './Step'
import { DateContainer, Group, Title, Subtitle } from './styles'
import { FormValue } from './types'

const Loader = styled(LoaderBase)`
  svg {
    display: block;
    margin: auto;
  }
`
const TimeInput = styled(Input)`
  display: block;
  width: 318px;
`

function setMomentTime(value: Moment | null, time: string) {
  const [hour, minute] = [...time.split(':').map(Number), 0]
  return moment(value || undefined).set({ hour, minute })
}

interface Props {
  formikProps: FormikProps<FormValue>
}

export default function Details({ formikProps }: Props) {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setTouched,
    handleBlur,
    handleChange,
  } = formikProps
  const dispatch = useDispatch()
  const bidPricingOptions = useSelector(rateTypesSelector)
  const isLoading = useSelector(rateTypesIsLoadingSelector)

  useEffect(() => {
    dispatch(fetchRateTypes())
  }, [dispatch])

  const handleFieldChange = (field: string) => (value: any) => {
    setTouched({ ...touched, [field]: true })
    setFieldValue(field, value)
  }
  const handleDeadlineDateChange = useCallback(
    (value: Moment | null) => {
      let dateWithTime = value
      if (value) {
        dateWithTime = setMomentTime(
          value,
          values.requestDeadline?.format('HH:mm')
        )
      }
      setFieldValue('requestDeadline', dateWithTime)
    },
    [values.requestDeadline, setFieldValue]
  )
  const handleDeadlineTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const time = event.currentTarget.value
      const value = setMomentTime(values.requestDeadline, time)
      setFieldValue('requestDeadline', value)
    },
    [values.requestDeadline, setFieldValue]
  )

  return (
    <Step
      title="Let’s start by setting up your RFP details"
      subtitle="You can edit the lanes at anytime during the process, but you may lose the bids"
    >
      <Group>
        <Label>
          RFP TITLE*
          <Input
            name="rfpTitle"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.rfpTitle}
            type="text"
            error={!!(touched.rfpTitle && errors.rfpTitle)}
          />
          <Error error={touched.rfpTitle ? errors.rfpTitle : ''} />
        </Label>
      </Group>
      <Group>
        <Label>
          PREFERRED BID PRICING*
          {isLoading ? (
            <Loader type="TailSpin" width={54} height={54} />
          ) : (
            <MultiSelect
              options={bidPricingOptions}
              value={values.bidPricing}
              handleChange={handleFieldChange('bidPricing')}
            />
          )}
        </Label>
      </Group>
      <Group>
        <DateContainer>
          <Label>
            CONTRACT START DATE
            <DayPicker
              date={values.contractStartDate}
              setDate={handleFieldChange('contractStartDate')}
              error={!!(touched.contractStartDate && errors.contractStartDate)}
            />
            <Error
              error={
                touched.contractStartDate
                  ? (errors.contractStartDate as any)
                  : ''
              }
            />
          </Label>
          <Label>
            CONTRACT END DATE
            <DayPicker
              date={values.contractEndDate}
              setDate={handleFieldChange('contractEndDate')}
              error={
                !!(
                  (touched.contractEndDate || touched.contractStartDate) &&
                  errors.contractEndDate
                )
              }
            />
            <Error
              error={
                touched.contractEndDate || touched.contractStartDate
                  ? (errors.contractEndDate as any)
                  : ''
              }
            />
          </Label>
        </DateContainer>
      </Group>
      <Group flexCenter>
        <div>
          <Title>
            <b>When is the deadline for the responses?</b>
          </Title>
          <Subtitle>
            This will be considered the end date for the first round
          </Subtitle>
        </div>
      </Group>
      <Group flexCenter>
        <DayPicker
          date={values.requestDeadline}
          setDate={handleDeadlineDateChange}
          error={!!errors.requestDeadline}
        />
      </Group>
      <Group flexCenter>
        <Label>
          TIME
          <TimeInput
            value={values.requestDeadline?.format('HH:mm')}
            onChange={handleDeadlineTimeChange}
            type="time"
            error={!!errors.requestDeadline}
          />
          <Error error={(errors.requestDeadline as any) || ''} />
        </Label>
      </Group>
    </Step>
  )
}
